import { useEffect, useRef, useState, useCallback } from 'react';
import { KpiStrip } from '@/components/KpiStrip';
import { Database, Route, BarChart3, Share2, TrendingUp, Zap, Target, Users, Star, Activity, MapPin } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';
gsap.registerPlugin(ScrollTrigger);

// ─── Miniature Slovenia border (same points as main map, scaled 1:1 to 220×140 viewBox) ────
const MiniMap = ({ visible }) => {
  const miniInformers = [
    { id:1, lat:46.05, lng:14.50, region:'Center' },
    { id:2, lat:46.55, lng:15.65, region:'Vzhod' },
    { id:3, lat:46.23, lng:15.26, region:'Vzhod' },
    { id:4, lat:45.55, lng:13.73, region:'Zahod' },
    { id:5, lat:46.50, lng:13.57, region:'Zahod' },
    { id:6, lat:46.24, lng:14.36, region:'Center' },
  ];
  const border = "M0.0,77.6 L4.4,86.1 L3.3,89.1 L14.3,88.2 L17.8,92.6 L26.9,93.7 L25.0,99.1 L11.3,109.7 L12.4,112.9 L9.4,116.7 L14.4,122.1 L24.9,119.8 L18.6,137.2 L20.5,143.3 L39.0,151.8 L50.6,166.7 L43.2,173.5 L23.6,166.9 L0.8,175.7 L27.9,192.3 L38.3,189.1 L40.9,193.4 L47.7,194.5 L50.2,190.8 L56.8,190.5 L54.6,183.8 L58.3,181.9 L70.9,187.9 L77.5,188.7 L80.3,183.8 L88.2,188.5 L93.5,186.2 L103.9,179.5 L104.9,170.1 L108.0,169.3 L111.3,161.4 L114.8,162.6 L113.6,167.5 L122.5,173.8 L122.5,181.0 L132.3,184.2 L134.3,190.0 L142.8,187.6 L144.9,180.6 L159.3,186.8 L167.8,195.1 L187.2,186.4 L179.4,179.8 L177.1,170.6 L189.1,164.5 L185.7,158.6 L183.7,161.6 L184.6,155.9 L181.4,161.3 L178.1,158.8 L175.2,155.2 L176.9,152.4 L195.1,145.0 L194.7,141.9 L200.5,141.2 L201.5,138.0 L211.1,141.6 L217.4,138.1 L214.4,135.1 L214.6,130.3 L217.2,128.0 L216.5,117.9 L219.5,111.6 L209.1,105.5 L206.8,97.9 L211.6,88.4 L222.8,89.5 L225.0,82.8 L245.8,76.1 L251.6,71.2 L249.8,64.8 L272.5,66.8 L266.9,50.7 L278.7,43.1 L293.0,53.4 L300.0,53.7 L293.2,49.8 L291.8,41.8 L280.9,32.6 L284.3,24.5 L278.8,23.1 L274.1,16.4 L273.6,10.6 L276.8,4.8 L266.1,0.0 L243.1,6.6 L243.3,18.2 L247.2,22.1 L248.0,29.6 L229.5,20.7 L206.7,25.0 L198.1,35.2 L189.9,29.6 L155.1,30.2 L149.4,36.9 L147.2,32.7 L143.8,36.8 L139.2,36.1 L134.4,49.2 L124.7,50.5 L123.3,55.4 L113.7,59.0 L110.7,67.6 L106.8,60.3 L102.8,62.1 L98.2,57.6 L73.4,59.4 L66.7,52.4 L31.5,47.4 L29.2,58.5 L20.7,58.6 L17.7,64.2 L5.9,69.3 L6.8,73.1 Z";
  const toSVG = (lat,lng) => ({
    x: ((lng-13.38)/(16.60-13.38))*300,
    y: ((46.88-lat)/(46.88-45.42))*195,
  });
  const clr = r => r==='Center'?'#C87533':r==='Zahod'?'#38BDF8':'#A78BFA';
  return (
    <svg viewBox="0 0 300 195" className="w-full h-auto">
      <path d={border} fill="rgba(200,117,51,0.07)" stroke="rgba(200,117,51,0.3)" strokeWidth="1" />
      {visible && miniInformers.map(inf => {
        const {x,y} = toSVG(inf.lat,inf.lng);
        const c = clr(inf.region);
        return (
          <g key={inf.id}>
            <circle cx={x} cy={y} r="4" fill={c} opacity="0.9"/>
            <circle cx={x} cy={y} r="8" fill="none" stroke={c} strokeWidth="1" opacity="0.4">
              <animate attributeName="r" values="4;14;4" dur={`${2+inf.id*0.4}s`} repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0;0.4" dur={`${2+inf.id*0.4}s`} repeatCount="indefinite"/>
            </circle>
          </g>
        );
      })}
    </svg>
  );
};

// ─── Animated iPad Screen (mini replica of IntelApp) ────────────────────────
const IPadScreen = ({ visible }) => {
  const [dcc, setDcc] = useState(114);
  const [feed, setFeed] = useState([
    { name:'Maja K.', loc:'Bar Nova, LJ', dcc:3, time:'14:22', region:'Center' },
    { name:'Luka N.', loc:'Hotel Maxi, MB', dcc:5, time:'14:18', region:'Vzhod' },
    { name:'Sara V.', loc:'Lounge X, KP', dcc:4, time:'14:11', region:'Zahod' },
  ]);
  const [activeTab, setActiveTab] = useState(0);
  const clr = r => r==='Center'?'#C87533':r==='Zahod'?'#38BDF8':'#A78BFA';

  useEffect(() => {
    if (!visible) return;
    const names=['Ana M.','Alen R.','Petra H.','Matej Z.','Nina K.'];
    const places=['Caffe Bar, LJ','Pub Mix, MB','Lounge Sud, CE','Sky Bar, KP','Bistro, NG'];
    const regions=['Center','Zahod','Vzhod'];
    const iv = setInterval(() => {
      const n={
        name:names[Math.floor(Math.random()*names.length)],
        loc:places[Math.floor(Math.random()*places.length)],
        dcc:Math.floor(Math.random()*4+1),
        time:new Date().toLocaleTimeString('sl-SI',{hour:'2-digit',minute:'2-digit'}),
        region:regions[Math.floor(Math.random()*regions.length)],
      };
      setDcc(d=>d+n.dcc);
      setFeed(f=>[n,...f.slice(0,4)]);
    }, 3500);
    return ()=>clearInterval(iv);
  }, [visible]);

  const tabs=['Dashboard','Field','HoReCa'];
  const bars=[55,72,40,85,67,90,50,75,88,62,80,70];

  return (
    <div className="h-full flex flex-col text-white overflow-hidden" style={{ background:'#080f1e', fontSize:'11px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom:'1px solid rgba(255,255,255,0.07)', background:'rgba(8,15,30,0.95)' }}>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background:'rgba(200,117,51,0.2)' }}>
            <Zap style={{ width:10,height:10,color:'#C87533' }}/>
          </div>
          <span className="font-bold tracking-tight" style={{ fontSize:10 }}>WIBE INTEL</span>
        </div>
        <div className="flex gap-0.5 p-0.5 rounded-lg" style={{ background:'rgba(255,255,255,0.05)' }}>
          {tabs.map((t,i)=>(
            <button key={t} onClick={()=>setActiveTab(i)}
              className="px-2 py-0.5 rounded-md transition-all"
              style={{ background:activeTab===i?'#C87533':'transparent', color:activeTab===i?'#fff':'rgba(255,255,255,0.4)', fontSize:9, fontFamily:'monospace' }}>
              {t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-green-400" style={{ animation:'pulse 2s infinite' }}/>
          <span style={{ fontSize:8, color:'#22C55E', fontFamily:'monospace' }}>LIVE</span>
        </div>
      </div>

      {activeTab===0 && (
        <div className="flex-1 overflow-hidden p-2 space-y-2">
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { label:'DCC danas', value:dcc, color:'#C87533' },
              { label:'Aktivni', value:'18/20', color:'#38BDF8' },
              { label:'Sentiment', value:'4.6★', color:'#A78BFA' },
            ].map((k,i)=>(
              <div key={i} className="rounded-lg p-2" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)' }}>
                <p style={{ fontSize:8, color:'rgba(255,255,255,0.35)', fontFamily:'monospace', marginBottom:2 }}>{k.label}</p>
                <p className="font-bold" style={{ color:k.color, fontSize:13 }}>{k.value}</p>
              </div>
            ))}
          </div>
          {/* Map */}
          <div className="rounded-lg p-2" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontSize:8, color:'rgba(255,255,255,0.3)', fontFamily:'monospace', marginBottom:4 }}>TERENSKA SLIKA</p>
            <MiniMap visible={visible}/>
          </div>
          {/* Chart */}
          <div className="rounded-lg p-2" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontSize:8, color:'rgba(255,255,255,0.3)', fontFamily:'monospace', marginBottom:4 }}>DCC PO URAH</p>
            <div className="flex items-end gap-0.5" style={{ height:32 }}>
              {bars.map((h,i)=>(
                <div key={i} className="flex-1 rounded-sm transition-all duration-700"
                  style={{ height:`${visible?h:0}%`, background:h>75?'#C87533':'rgba(200,117,51,0.3)', transitionDelay:`${i*40}ms` }}/>
              ))}
            </div>
          </div>
          {/* Feed */}
          <div className="space-y-1">
            {feed.map((item,i)=>(
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
                style={{ background:i===0?'rgba(200,117,51,0.1)':'rgba(255,255,255,0.02)', border:`1px solid ${i===0?'rgba(200,117,51,0.2)':'transparent'}`, transition:'all 0.4s' }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background:`${clr(item.region)}20`, color:clr(item.region), fontSize:8, fontWeight:'bold' }}>
                  {item.name.slice(0,2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p style={{ fontSize:9, color:'rgba(255,255,255,0.8)', fontWeight:600 }}>{item.name}</p>
                  <p style={{ fontSize:8, color:'rgba(255,255,255,0.3)' }} className="truncate">{item.loc}</p>
                </div>
                <span style={{ fontSize:11, fontWeight:'bold', color:'#C87533' }}>+{item.dcc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab===1 && (
        <div className="flex-1 p-2 space-y-2">
          {/* DCC ring mini */}
          <div className="rounded-lg p-3 flex items-center gap-4" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)' }}>
            <svg width="52" height="52" viewBox="0 0 52 52">
              <circle cx="26" cy="26" r="20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4"/>
              <circle cx="26" cy="26" r="20" fill="none" stroke="#C87533" strokeWidth="4"
                strokeDasharray="125.6" strokeDashoffset={visible?'25':'125.6'} strokeLinecap="round"
                transform="rotate(-90 26 26)" style={{ transition:'stroke-dashoffset 1.2s ease 0.3s', filter:'drop-shadow(0 0 3px #C87533)' }}/>
              <text x="26" y="29" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">17</text>
            </svg>
            <div>
              <p style={{ fontSize:13, fontWeight:'bold' }}>17 <span style={{ fontSize:9, color:'rgba(255,255,255,0.3)' }}>/20 DCC</span></p>
              <p style={{ fontSize:8, color:'rgba(255,255,255,0.35)', fontFamily:'monospace' }}>CILJ IZMENE · 85%</p>
            </div>
          </div>
          {/* Locations */}
          {[
            { name:'Bar Fusion',       addr:'Trubarjeva 22, LJ', s:'current', dcc:null },
            { name:'Caffe Metropolis', addr:'Cankarjeva 5, LJ',  s:'done',    dcc:4 },
            { name:'Hotel Arc',        addr:'Prešernov trg, LJ', s:'done',    dcc:6 },
            { name:'Lounge Sky',       addr:'Šmartinska 10, LJ', s:'todo',    dcc:null },
            { name:'Pub Central',      addr:'Mestni trg 2, LJ',  s:'todo',    dcc:null },
          ].map((l,i)=>(
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
              style={{ background:l.s==='current'?'rgba(200,117,51,0.1)':l.s==='done'?'rgba(255,255,255,0.01)':'rgba(255,255,255,0.025)', border:`1px solid ${l.s==='current'?'rgba(200,117,51,0.25)':'transparent'}`, opacity:l.s==='done'?0.55:1 }}>
              <div style={{ width:8,height:8,borderRadius:'50%',flexShrink:0, background:l.s==='current'?'#C87533':l.s==='done'?'#22C55E':'rgba(255,255,255,0.2)' }}/>
              <div className="flex-1 min-w-0">
                <p style={{ fontSize:10, fontWeight:600, color:l.s==='current'?'#f5a96a':'white' }} className="truncate">{l.name}</p>
                <p style={{ fontSize:8, color:'rgba(255,255,255,0.3)' }} className="truncate">{l.addr}</p>
              </div>
              {l.dcc && <span style={{ fontSize:10, fontWeight:'bold', color:'#22C55E' }}>+{l.dcc}</span>}
            </div>
          ))}
        </div>
      )}
      {activeTab===2 && (
        <div className="flex-1 p-2 space-y-1.5">
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg mb-2" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)' }}>
            <span style={{ fontSize:9, color:'rgba(255,255,255,0.3)', fontFamily:'monospace' }}>🔍  Išči lokacijo...</span>
          </div>
          {[
            { name:'Bar Fusion', city:'Ljubljana', type:'Bar', dcc:23, status:'Prioriteta' },
            { name:'Hotel Maxi', city:'Maribor',   type:'Hotel',dcc:18, status:'Obiskan' },
            { name:'Lounge Sky', city:'Koper',     type:'Club', dcc:31, status:'Prioriteta' },
            { name:'Caffe Mix',  city:'Celje',     type:'Café', dcc:12, status:'Čakanje' },
            { name:'Pub Central',city:'Ljubljana', type:'Pub',  dcc:9,  status:'Redni' },
            { name:'Sky Bar',    city:'Novo M.',   type:'Bar',  dcc:15, status:'Obiskan' },
          ].map((l,i)=>{
            const sc = l.status==='Obiskan'?{bg:'rgba(34,197,94,0.12)',c:'#22C55E'}:l.status==='Prioriteta'?{bg:'rgba(200,117,51,0.12)',c:'#C87533'}:{bg:'rgba(56,189,248,0.12)',c:'#38BDF8'};
            return (
              <div key={i} className="grid items-center px-2 py-1.5 rounded-lg" style={{ gridTemplateColumns:'1fr auto auto', gap:8, background:i%2===0?'transparent':'rgba(255,255,255,0.015)', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                <div>
                  <p style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.85)' }}>{l.name}</p>
                  <p style={{ fontSize:8, color:'rgba(255,255,255,0.3)' }}>{l.city} · {l.type}</p>
                </div>
                <span style={{ fontSize:11, fontWeight:'bold', color:'#C87533' }}>{l.dcc}</span>
                <span style={{ fontSize:8, padding:'2px 6px', borderRadius:999, background:sc.bg, color:sc.c, fontFamily:'monospace', whiteSpace:'nowrap' }}>{l.status}</span>
              </div>
            );
          })}
          <p style={{ fontSize:8, color:'rgba(255,255,255,0.2)', textAlign:'center', paddingTop:4, fontFamily:'monospace' }}>1.500+ primernih lokacij v bazi</p>
        </div>
      )}
    </div>
  );
};

// ─── Realistic iPad Frame ────────────────────────────────────────────────────
const IPad = ({ visible }) => (
  <div className="relative mx-auto select-none" style={{ width:'100%', maxWidth:380 }}>
    {/* Ambient glow */}
    <div className="absolute -inset-6 rounded-[50px] blur-3xl pointer-events-none"
      style={{ background:'radial-gradient(ellipse, rgba(200,117,51,0.12) 0%, transparent 70%)' }}/>
    {/* Body */}
    <div className="relative rounded-[32px] shadow-[0_30px_80px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.08)]"
      style={{ background:'linear-gradient(160deg,#2a2a2e 0%,#1c1c1e 40%,#141416 100%)', padding:'14px 12px 18px' }}>
      {/* Top bezel */}
      <div className="flex items-center justify-center mb-1.5" style={{ height:18 }}>
        {/* Front camera */}
        <div className="w-2 h-2 rounded-full" style={{ background:'#0a0a0c', border:'1px solid rgba(255,255,255,0.06)', boxShadow:'inset 0 0 0 0.5px rgba(255,255,255,0.04)' }}>
          <div className="w-1 h-1 rounded-full mx-auto mt-0.5" style={{ background:'rgba(56,189,248,0.3)' }}/>
        </div>
      </div>
      {/* Screen */}
      <div className="rounded-2xl overflow-hidden relative" style={{ aspectRatio:'3/4.2', boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.05)' }}>
        {/* Status bar */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 py-1"
          style={{ background:'rgba(8,15,30,0.9)', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
          <span style={{ fontSize:8, color:'rgba(255,255,255,0.5)', fontFamily:'monospace' }}>9:41</span>
          <div className="flex items-center gap-1">
            {/* Signal bars */}
            {[3,4,5,6].map(h=>(
              <div key={h} className="w-0.5 rounded-sm" style={{ height:h, background:'rgba(255,255,255,0.5)' }}/>
            ))}
            <div className="w-4 h-2 rounded-sm ml-1 relative" style={{ border:'1px solid rgba(255,255,255,0.35)' }}>
              <div className="absolute inset-0.5 rounded-sm" style={{ background:'rgba(255,255,255,0.6)', width:'70%' }}/>
            </div>
          </div>
        </div>
        {/* App content (padded for status bar) */}
        <div className="h-full" style={{ paddingTop:18 }}>
          <IPadScreen visible={visible}/>
        </div>
      </div>
      {/* Bottom bezel with home indicator */}
      <div className="flex justify-center mt-2.5">
        <div className="w-16 h-1 rounded-full" style={{ background:'rgba(255,255,255,0.15)' }}/>
      </div>
    </div>
    {/* Side buttons (right) */}
    <div className="absolute right-0 top-24 w-0.5 h-10 rounded-l-sm" style={{ background:'rgba(255,255,255,0.12)', right:-2 }}/>
    <div className="absolute right-0 top-40 w-0.5 h-14 rounded-l-sm" style={{ background:'rgba(255,255,255,0.12)', right:-2 }}/>
    {/* Volume buttons (left) */}
    <div className="absolute left-0 top-20 w-0.5 h-8 rounded-r-sm" style={{ background:'rgba(255,255,255,0.12)', left:-2 }}/>
    <div className="absolute left-0 top-32 w-0.5 h-8 rounded-r-sm" style={{ background:'rgba(255,255,255,0.12)', left:-2 }}/>
    {/* Screen reflection */}
    <div className="absolute rounded-[32px] pointer-events-none overflow-hidden inset-0"
      style={{ background:'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%)', borderRadius:32 }}/>
  </div>
);

// ─── Section ─────────────────────────────────────────────────────────────────
export const IntelSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const ipadRef    = useRef(null);
  const [visible, setVisible]   = useState(false);
  const { lang } = useLanguage();
  const tx = t(lang).intel;
  const FEAT_ICONS = [Database, Route, BarChart3, Share2, Zap];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 65%',
          once: true,
          onEnter: () => setVisible(true),
        }
      });
      tl.from('.intel-badge',   { opacity:0, y:20, duration:0.5 })
        .from('.intel-title',   { opacity:0, y:30, duration:0.6 }, '-=0.2')
        .from('.intel-feature', { opacity:0, x:-24, duration:0.5, stagger:0.1 }, '-=0.3')
        .from('.intel-kpis',    { opacity:0, y:20, duration:0.5 }, '-=0.2')
        .from('.intel-dcc',     { opacity:0, y:20, duration:0.5 }, '-=0.2')
        .from(ipadRef.current,  { opacity:0, x:60, rotateY:-8, duration:0.9, ease:'power3.out' }, 0.1);
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="intel" className="scroll-section min-h-screen flex items-center px-4 py-20">
      <div className="section-content max-w-[1280px] mx-auto w-full">

        <div className="grid gap-16 lg:grid-cols-2 items-start">

          {/* ── Left: Copy ─────────────────────────────────────────────── */}
          <div ref={contentRef}>
            <div className="intel-badge flex items-center gap-3 mb-8">
              <span className="w-8 h-px bg-[#C87533]"/>
              <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">{tx.label}</span>
            </div>

            <h2 className="intel-title font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 text-white leading-tight">
              {tx.headline1}<br/>
              <span className="text-[#C87533]">{tx.headline2}</span> {tx.headlineAccent}
            </h2>
            <p className="intel-title text-white/45 text-base mb-12 leading-relaxed max-w-md">
              {tx.sub}
            </p>

            <div className="intel-feature space-y-6 mb-12">
              {tx.feats.map((item, i) => {
                const Icon = FEAT_ICONS[i];
                return (
                  <div key={i} className="intel-feature flex gap-4 items-start group">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                      style={{ background:'rgba(200,117,51,0.1)', border:'1px solid rgba(200,117,51,0.2)' }}>
                      <Icon className="w-4 h-4 text-[#C87533]"/>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-0.5">{item.title}</h3>
                      <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="intel-kpis mb-12">
              <KpiStrip cols={3} kpis={[
                { value:15,   label: tx.kpi1 },
                { value:20,   label: tx.kpi2 },
                { value:1500, suffix:'+', label: tx.kpi3 },
              ]}/>
            </div>
          </div>

          {/* ── Right: iPad ─────────────────────────────────────────────── */}
          <div ref={ipadRef} className="flex justify-center lg:justify-end perspective-1000">
            <IPad visible={visible}/>
          </div>
        </div>

      </div>
    </section>
  );
};
